"use client";

import React, { useState, useEffect } from "react";
import { authHelpers } from "../../../firebaseClient";
import {
  getNesolearnId,
  claimNesolearnId,
  identifierToLookupId,
  resolveNesolearnIdToEmail,
  normalizeUsername,
  isValidUsername,
} from "../../../utils/nesolearnIdHelpers";

export default function AuthSwitch({ onSuccess }) {
  const [isSignUp, setIsSignUp] = useState(false);

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [loginError, setLoginError] = useState("");
  const [signupError, setSignupError] = useState("");
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Claim-ID modal state
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimUsername, setClaimUsername] = useState("");
  const [claimError, setClaimError] = useState("");
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimedId, setClaimedId] = useState("");
  const [copied, setCopied] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    const container = document.querySelector(".auth-switch-container");
    if (!container) return;
    if (isSignUp) container.classList.add("sign-up-mode");
    else container.classList.remove("sign-up-mode");
  }, [isSignUp]);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
    if (loginError) setLoginError("");
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({ ...prev, [name]: value }));
    if (name === "password") setPasswordStrength(calculatePasswordStrength(value));
    if (signupError) setSignupError("");
  };

  // After any successful auth, checks whether this account already has a
  // Nesolearn ID. If not, opens the claim modal instead of continuing
  // straight through — same gate for signup, login, and Google.
  const proceedAfterAuth = async (user) => {
    setPendingUser(user);
    const { nesolearnId } = await getNesolearnId(user.uid);

    if (nesolearnId) {
      if (onSuccess) onSuccess();
      return;
    }

    setClaimUsername("");
    setClaimError("");
    setShowClaimModal(true);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!loginData.email || !loginData.password) {
      setLoginError("Please enter your email/ID and password.");
      return;
    }

    let emailToUse = loginData.email.trim();
    const lookupId = identifierToLookupId(emailToUse);

    if (lookupId) {
      const { email: resolvedEmail, error: resolveError } = await resolveNesolearnIdToEmail(lookupId);
      if (resolveError || !resolvedEmail) {
        setLoginError("No account found with that Nesolearn ID.");
        return;
      }
      emailToUse = resolvedEmail;
    }

    setIsLoginLoading(true);
    const { user, error } = await authHelpers.signIn(emailToUse, loginData.password);
    setIsLoginLoading(false);

    if (error) {
      const code = error.code || "";
      if (code.includes("user-not-found") || code.includes("wrong-password") || code.includes("invalid-credential")) {
        setLoginError("Invalid email/ID or password. Please try again.");
      } else if (code.includes("too-many-requests")) {
        setLoginError("Too many failed attempts. Please try again later.");
      } else {
        setLoginError(error.message || "Sign in failed. Please try again.");
      }
      return;
    }

    if (user) await proceedAfterAuth(user);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setSignupError("");

        if (!signupData.name || !signupData.email || !signupData.password || !signupData.confirmPassword) {
      setSignupError("Please fill in all fields.");
      return;
    }
    if (signupData.password.length < 8) {
      setSignupError("Password must be at least 8 characters.");
      return;
    }
    if (signupData.password !== signupData.confirmPassword) {
      setSignupError("Passwords do not match.");
      return;
    }

    setIsSignupLoading(true);
        const { user, error } = await authHelpers.signUp(signupData.email, signupData.password, signupData.name.trim());
    setIsSignupLoading(false);

    if (error) {
      const code = error.code || "";
      if (code.includes("email-already-in-use")) {
        setSignupError("An account with this email already exists.");
      } else if (code.includes("weak-password")) {
        setSignupError("Password is too weak. Please choose a stronger one.");
      } else if (code.includes("invalid-email")) {
        setSignupError("Please enter a valid email address.");
      } else {
        setSignupError(error.message || "Registration failed. Please try again.");
      }
      return;
    }

    if (user) await proceedAfterAuth(user);
  };

    const handleGoogleAuth = async () => {
    setIsGoogleLoading(true);
    const { user, error } = await authHelpers.signInWithGoogle();
    setIsGoogleLoading(false);

    if (error) {
      if (error.code === "auth/popup-closed-by-user") return;
      console.error("Google sign-in error:", error.code, error.message);
      setLoginError(`Failed to sign in with Google (${error.code || "unknown error"}).`);
      return;
    }

    if (user) await proceedAfterAuth(user);
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    setClaimError("");

    const clean = normalizeUsername(claimUsername);
    if (!isValidUsername(clean)) {
      setClaimError("Use 3-20 characters, starting with a letter — letters, numbers, _ or - only.");
      return;
    }

    setIsClaiming(true);
    const { nesolearnId, error } = await claimNesolearnId(pendingUser?.uid, pendingUser?.email, clean);
    setIsClaiming(false);

    if (error) {
      setClaimError(error.message || "Couldn't claim that ID. Try another.");
      return;
    }

    setClaimedId(nesolearnId);
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(claimedId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard API can fail on non-HTTPS/older browsers — ID is still visible on screen
    }
  };
  
  const handleContinueAfterClaim = () => {
    setShowClaimModal(false);
    if (onSuccess) onSuccess();
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;800&family=Space+Mono&display=swap');

        .auth-switch-page {
          background: #1b1b1b;
          min-height: calc(100vh - 4rem);
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 20px;
          font-family: 'Inter', sans-serif;
        }

        .auth-switch-container {
          position: relative;
          width: 100%;
          max-width: 900px;
          height: 610px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }

        .forms-container {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }

        .signin-signup {
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
          left: 75%;
          width: 50%;
          transition: 1s 0.7s ease-in-out;
          display: grid;
          grid-template-columns: 1fr;
          z-index: 5;
        }

        .auth-switch-container form {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          padding: 0 4rem;
          transition: all 0.2s 0.7s;
          overflow: hidden;
          grid-column: 1 / 2;
          grid-row: 1 / 2;
        }

        .auth-switch-container form.sign-up-form {
          opacity: 0;
          z-index: 1;
        }

        .auth-switch-container form.sign-in-form {
          z-index: 2;
        }

        .auth-switch-container .title {
          font-size: 2.2rem;
          color: #444;
          margin-bottom: 10px;
          font-weight: 800;
          font-family: 'Inter', sans-serif;
          letter-spacing: -1px;
        }

          .auth-switch-container .input-field {
          max-width: 380px;
          width: 100%;
          background-color: #f0f0f0;
          margin: 8px 0;
          height: 52px;
          border-radius: 52px;
          display: flex;
          align-items: center;
          padding: 0 0.4rem;
          position: relative;
          border: 2px solid transparent;
          box-sizing: border-box;
          overflow: hidden;
          transition: background-color 0.3s ease, border-color 0.3s ease;
        }

        .auth-switch-container .input-field:focus-within {
          background-color: #e8e8e8;
          border-color: #344532;
        }

          .auth-switch-container .input-field i {
          flex: 0 0 auto;
          width: 15%;
          min-width: 44px;
          text-align: center;
          line-height: 52px;
          color: #666;
          transition: 0.5s;
          font-size: 1.1rem;
          font-style: normal;
        }

          .auth-switch-container .input-field input {
          background: none;
          outline: none;
          border: none;
          line-height: 1;
          font-weight: 500;
          font-size: 1rem;
          color: #333;
          flex: 1 1 auto;
          min-width: 0;
          border-radius: 0 52px 52px 0;
        }

        .auth-switch-container .input-field input:-webkit-autofill,
        .auth-switch-container .input-field input:-webkit-autofill:hover,
        .auth-switch-container .input-field input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px #f0f0f0 inset;
          -webkit-text-fill-color: #333;
          caret-color: #333;
          transition: background-color 9999s ease-in-out 0s;
        }

                .auth-switch-container .input-field input::placeholder {
          color: #aaa;
          font-weight: 400;
        }

        .auth-switch-container .input-field input[type="password"],
        .auth-switch-container .input-field input[data-password-field="true"] {
          padding-right: 34px;
        }

        .auth-switch-container .password-toggle {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #888;
          font-size: 1rem;
          line-height: 1;
          padding: 4px;
        }

        .auth-switch-container .password-toggle:hover {
          color: #444;
        }

        .auth-switch-container .id-hint {
          max-width: 380px;
          width: 100%;
          color: #888;
          font-size: 0.72rem;
          margin-top: -6px;
          margin-bottom: 6px;
          font-family: 'Space Mono', monospace;
        }

        .auth-switch-container .error-text {
          max-width: 380px;
          width: 100%;
          color: #e74c3c;
          font-size: 0.8rem;
          margin-top: -4px;
          margin-bottom: 6px;
        }

        .auth-switch-container .strength-row {
          max-width: 380px;
          width: 100%;
          display: flex;
          gap: 4px;
          margin: 4px 0 10px;
        }

        .auth-switch-container .strength-bar {
          height: 4px;
          flex: 1;
          border-radius: 4px;
          background: #eee;
        }

        .auth-switch-container .btn {
          width: 150px;
          background-color: #0a0a0a;
          border: none;
          outline: none;
          height: 49px;
          border-radius: 49px;
          color: #fff;
          text-transform: uppercase;
          font-weight: 600;
          margin: 10px 0;
          cursor: pointer;
          transition: 0.5s;
          font-size: 0.9rem;
        }

        .auth-switch-container .btn:hover {
          background-color: #1a1a1a;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
        }

        .auth-switch-container .btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .panels-container {
          position: absolute;
          height: 100%;
          width: 100%;
          top: 0;
          left: 0;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        }

        .panel {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: space-around;
          text-align: center;
          z-index: 6;
        }

        .left-panel {
          pointer-events: all;
          padding: 3rem 17% 2rem 12%;
        }

        .right-panel {
          pointer-events: none;
          padding: 3rem 12% 2rem 17%;
        }

        .panel .content {
          color: #fff;
          transition: transform 0.9s ease-in-out;
          transition-delay: 0.6s;
        }

        .panel h3 {
          font-weight: 800;
          line-height: 1;
          font-size: 1.5rem;
          margin-bottom: 10px;
          font-family: 'Inter', sans-serif;
          letter-spacing: -0.5px;
        }

        .panel p {
          font-size: 0.95rem;
          padding: 0.7rem 0;
          font-family: 'Inter', sans-serif;
          font-weight: 300;
        }

        .auth-switch-container .btn.transparent {
          margin: 0;
          background: none;
          border: 2px solid #fff;
          width: 130px;
          height: 41px;
          font-weight: 600;
          font-size: 0.8rem;
        }

        .auth-switch-container .btn.transparent:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }

        .right-panel .content {
          transform: translateX(800px);
        }

        .auth-switch-container.sign-up-mode:before {
          transform: translate(100%, -50%);
          right: 52%;
        }

        .auth-switch-container.sign-up-mode .left-panel .content {
          transform: translateX(-800px);
        }

        .auth-switch-container.sign-up-mode .signin-signup {
          left: 25%;
        }

        .auth-switch-container.sign-up-mode form.sign-up-form {
          opacity: 1;
          z-index: 2;
        }

        .auth-switch-container.sign-up-mode form.sign-in-form {
          opacity: 0;
          z-index: 1;
        }

        .auth-switch-container.sign-up-mode .right-panel .content {
          transform: translateX(0%);
        }

        .auth-switch-container.sign-up-mode .left-panel {
          pointer-events: none;
        }

        .auth-switch-container.sign-up-mode .right-panel {
          pointer-events: all;
        }

        .auth-switch-container:before {
          content: "";
          position: absolute;
          height: 2000px;
          width: 2000px;
          top: -10%;
          right: 48%;
          transform: translateY(-50%);
          background: linear-gradient(-45deg, #1b1b1b 0%, #1a1a1a 100%);
          transition: 1.8s ease-in-out;
          border-radius: 50%;
          z-index: 6;
        }

        .auth-switch-container .social-text {
          padding: 0.5rem 0;
          font-size: 0.9rem;
          color: #666;
          font-family: 'Space Mono', monospace;
        }

        .auth-switch-container .social-media {
          display: flex;
          justify-content: center;
          gap: 15px;
        }

        .auth-switch-container .social-icon {
          height: 46px;
          width: 46px;
          display: flex;
          justify-content: center;
          align-items: center;
          border: 1px solid #ddd;
          border-radius: 50%;
          color: #344532;
          font-size: 1.2rem;
          transition: 0.3s;
          cursor: pointer;
          background: none;
        }

        .auth-switch-container .social-icon:hover {
          border-color: #464c4d;
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }

        .auth-switch-container .social-icon:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .id-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          padding: 20px;
        }

        .id-modal-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          max-width: 400px;
          width: 100%;
          text-align: center;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
        }

        .id-modal-icon {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
        }

        .id-modal-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: #444;
          margin-bottom: 0.5rem;
        }

        .id-modal-subtitle {
          font-size: 0.85rem;
          color: #777;
          margin-bottom: 1.25rem;
          line-height: 1.4;
          font-family: 'Space Mono', monospace;
        }

        .id-claim-input-field {
          display: flex;
          align-items: center;
          background: #f0f0f0;
          border-radius: 12px;
          padding: 0 1rem;
          height: 52px;
          margin-bottom: 0.5rem;
        }

        .id-claim-input-field input {
          flex: 1;
          background: none;
          border: none;
          outline: none;
          font-size: 1rem;
          font-weight: 600;
          color: #333;
          text-align: right;
        }

        .id-claim-input-field .suffix {
          color: #888;
          font-weight: 600;
          margin-left: 4px;
        }

        .id-modal-id-display {
          font-size: 1.6rem;
          font-weight: 700;
          color: #344532;
          background: #f0f0f0;
          border-radius: 12px;
          padding: 0.75rem;
          margin-bottom: 1.25rem;
          word-break: break-all;
        }

        .id-modal-continue {
          display: block;
          width: 100%;
          background: none;
          border: none;
          color: #888;
          font-size: 0.85rem;
          margin-top: 1rem;
          cursor: pointer;
          text-decoration: underline;
        }

        @media (max-width: 870px) {
          .auth-switch-container {
            min-height: 800px;
            height: auto;
          }
          .signin-signup {
            width: 100%;
            top: 95%;
            transform: translate(-50%, -100%);
            transition: 1s 0.8s ease-in-out;
          }
          .signin-signup,
          .auth-switch-container.sign-up-mode .signin-signup {
            left: 50%;
          }
          .panels-container {
            grid-template-columns: 1fr;
            grid-template-rows: 1fr 2fr 1fr;
          }
          .panel {
            flex-direction: row;
            justify-content: space-around;
            align-items: center;
            padding: 2.5rem 8%;
            grid-column: 1 / 2;
          }
          .right-panel {
            grid-row: 3 / 4;
          }
          .left-panel {
            grid-row: 1 / 2;
          }
          .panel .content {
            padding-right: 15%;
            transition: transform 0.9s ease-in-out;
            transition-delay: 0.8s;
          }
          .panel h3 {
            font-size: 1.2rem;
          }
          .panel p {
            font-size: 0.7rem;
            padding: 0.5rem 0;
          }
          .auth-switch-container .btn.transparent {
            width: 110px;
            height: 35px;
            font-size: 0.7rem;
          }
          .auth-switch-container:before {
            width: 1500px;
            height: 1500px;
            transform: translateX(-50%);
            left: 30%;
            bottom: 68%;
            right: initial;
            top: initial;
            transition: 2s ease-in-out;
          }
          .auth-switch-container.sign-up-mode:before {
            transform: translate(-50%, 100%);
            bottom: 32%;
            right: initial;
          }
          .auth-switch-container.sign-up-mode .left-panel .content {
            transform: translateY(-300px);
          }
          .auth-switch-container.sign-up-mode .right-panel .content {
            transform: translateY(0px);
          }
          .right-panel .content {
            transform: translateY(300px);
          }
          .auth-switch-container.sign-up-mode .signin-signup {
            top: 5%;
            transform: translate(-50%, 0);
          }
        }

        @media (max-width: 570px) {
          .auth-switch-container form {
            padding: 0 1.5rem;
          }
          .panel .content {
            padding: 0.5rem 1rem;
          }
        }
      `}</style>

      {showClaimModal && (
        <div className="id-modal-overlay">
          <div className="id-modal-card">
            {!claimedId ? (
              <form onSubmit={handleClaimSubmit}>
                <div className="id-modal-icon">🪪</div>
                <h2 className="id-modal-title">Claim Your Nesolearn ID</h2>
                <p className="id-modal-subtitle">
                  This is your digital identity across Nesolearn, pick a username. You can use it to log in instead of your email.
                </p>
                <div className="id-claim-input-field">
                  <input
                    type="text"
                    placeholder="yourname"
                    value={claimUsername}
                    onChange={(e) => setClaimUsername(e.target.value)}
                    autoFocus
                  />
                  <span className="suffix">.nesol</span>
                </div>
                {claimError && <p className="error-text" style={{ margin: "0 auto 10px" }}>{claimError}</p>}
                                <button type="submit" className="btn solid" disabled={isClaiming} style={{ width: "auto", padding: "0 1.5rem" }}>
                  {isClaiming ? "Checking..." : "Claim ID"}
                </button>
              </form>
            ) : (
              <>
                <h2 className="id-modal-title">ID Claimed!</h2>
                <p className="id-modal-subtitle">Save this, you can use it to log in anytime.</p>
                <div className="id-modal-id-display">{claimedId}</div>
                <button type="button" className="btn solid" onClick={handleCopyId} style={{ width: "auto", padding: "0 1.5rem" }}>
                  {copied ? "Copied!" : "Copy ID"}
                </button>
                <button type="button" className="id-modal-continue" onClick={handleContinueAfterClaim}>
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="auth-switch-page">
        <div className="auth-switch-container">
          <div className="forms-container">
            <div className="signin-signup">
              {/* Sign In Form */}
              <form className="sign-in-form" onSubmit={handleLoginSubmit}>
                <h2 className="title">Sign in</h2>
                {loginError && <p className="error-text">{loginError}</p>}
                <div className="input-field">
                  <i>🪪</i>
                  <input
                    type="text"
                    name="email"
                    placeholder="Email or Nesolearn ID"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    autoComplete="username"
                  />
                </div>
                <p className="id-hint">Sign in with your email, or your Nesolearn ID (e.g. yourname.nesol)</p>
                                <div className="input-field">
                  <i>🔒</i>
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    autoComplete="current-password"
                    data-password-field="true"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowLoginPassword((v) => !v)}
                    aria-label={showLoginPassword ? "Hide password" : "Show password"}
                  >
                    {showLoginPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                <input type="submit" value={isLoginLoading ? "Signing in..." : "Login"} className="btn solid" disabled={isLoginLoading} />
                <p className="social-text">Or sign in with</p>
                <div className="social-media">
                  <GoogleIcon onClick={handleGoogleAuth} loading={isGoogleLoading} />
                </div>
              </form>

              {/* Sign Up Form */}
                            <form className="sign-up-form" onSubmit={handleSignupSubmit}>
                <h2 className="title">Sign up</h2>
                {signupError && <p className="error-text">{signupError}</p>}
                <div className="input-field">
                  <i>👤</i>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={signupData.name}
                    onChange={handleSignupChange}
                    autoComplete="name"
                  />
                </div>
                <div className="input-field">
                  <i>📧</i>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={signupData.email}
                    onChange={handleSignupChange}
                    autoComplete="email"
                  />
                </div>
                                <div className="input-field">
                  <i>🔒</i>
                  <input
                    type={showSignupPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={signupData.password}
                    onChange={handleSignupChange}
                    autoComplete="new-password"
                    data-password-field="true"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowSignupPassword((v) => !v)}
                    aria-label={showSignupPassword ? "Hide password" : "Show password"}
                  >
                    {showSignupPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                {signupData.password && (
                  <div className="strength-row">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="strength-bar"
                        style={{
                          background:
                            i < passwordStrength
                              ? passwordStrength >= 4
                                ? "#2ecc71"
                                : passwordStrength >= 3
                                ? "#2ecc71"
                                : "#f39c12"
                              : "#eee",
                        }}
                      />
                    ))}
                  </div>
                )}
                                <div className="input-field">
                  <i>🔒</i>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={signupData.confirmPassword}
                    onChange={handleSignupChange}
                    autoComplete="new-password"
                    data-password-field="true"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? "🙈" : "👁️"}
                  </button>
                </div>
                <input type="submit" value={isSignupLoading ? "Creating account..." : "Sign up"} className="btn" disabled={isSignupLoading} />
                <p className="social-text">Or sign up with</p>
                <div className="social-media">
                  <GoogleIcon onClick={handleGoogleAuth} loading={isGoogleLoading} />
                </div>
              </form>
            </div>
          </div>

          <div className="panels-container">
            <div className="panel left-panel">
              <div className="content">
                <h3>New here?</h3>
                <p>Join us today and discover a world of possibilities. Create your account in seconds!</p>
                <button type="button" className="btn transparent" onClick={() => setIsSignUp(true)}>
                  Sign up
                </button>
              </div>
            </div>

            <div className="panel right-panel">
              <div className="content">
                <h3>One of us?</h3>
                <p>Welcome back! Sign in to continue your journey with us.</p>
                <button type="button" className="btn transparent" onClick={() => setIsSignUp(false)}>
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Only Google is wired to a real provider — Firebase isn't configured for
// Facebook/Twitter/LinkedIn, so those icons from the original mockup were
// dropped rather than shipped as dead buttons.
function GoogleIcon({ onClick, loading }) {
  return (
    <button type="button" className="social-icon" onClick={onClick} disabled={loading} aria-label="Continue with Google">
      {loading ? (
        <span style={{ fontSize: "0.9rem" }}>...</span>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      )}
    </button>
  );
}